import ErrorBoundary from '@/components/ErrorBoundary';
import { StandaloneArticleDisclosureLabelProps } from '@/components/element/ArticleDisclosureLabel';
import { LabradorComponent, StandaloneComponentProps, StandaloneComponentWithRef } from '@/types/component';
import { Content } from '@/types/content';
import { EmptyObject } from '@/types/utils';
import mergeProps, { mergeOptions } from 'lib/utils/mergeProps';
import component, { renderBySelector, standaloneComponent } from 'modules/dynamic-components';
import { ReactNode, forwardRef } from 'react';
import { brandConfig } from '../../content/RecommendedArticles/config';
import { StandaloneLoopAroundProps } from '../../element/LoopAround';
import { Article } from './Article';

const ArticleDisclosureLabel = standaloneComponent<StandaloneArticleDisclosureLabelProps>(
  'ElementArticleDisclosureLabel'
);
const LoopAround = standaloneComponent<StandaloneLoopAroundProps>('ElementLoopAround');

const Strossle = component<EmptyObject>('ElementStrossle');
const RecommendedArticles = component<EmptyObject>('ContentRecommendedArticles');
const SecondNativeArticle = component<EmptyObject>('ElementSecondNativeArticle');

type ExtendedArticleProps = {
  $articleDisclosureLabel?: StandaloneArticleDisclosureLabelProps['options'];
  $badge?: StandaloneLoopAroundProps['options'];
};

export interface StandaloneArticleProps extends StandaloneComponentProps<typeof Article, ExtendedArticleProps> {
  articleDisclosureLabels?: string[];
  badge?: StandaloneLoopAroundProps;
  bodytext?: {
    before?: ReactNode;
    after?: ReactNode;
  };
  descendants?: Content[];
  hide?: {
    trendingMenu?: boolean;
    breakingNews?: boolean;
    badge?: boolean;
    recommendedArticles?: boolean;
    secondNativeArticle?: boolean;
  };
}

export const StandaloneArticle: StandaloneComponentWithRef<HTMLElement, StandaloneArticleProps> = forwardRef(
  ({ articleDisclosureLabels, badge, bodytext, descendants, hide, options, ...props }, ref) => {
    const { $bodytext, $recommended, $articleDisclosureLabel, $badge, ...$base } = options ?? {};
    const showMoreRecommendedArticles = brandConfig.showMoreBelowArticle;

    return (
      <ErrorBoundary>
        <section>
          <ArticleDisclosureLabel content={articleDisclosureLabels?.join('<br>')} options={$articleDisclosureLabel} />

          {!hide?.badge && badge?.fragment && (
            <LoopAround
              {...mergeProps(
                {
                  length: 40,
                  options: mergeOptions(
                    {
                      className: 'w-screen relative left-1/2 -translate-x-1/2',
                    },
                    $badge
                  ),
                },
                badge,
                {
                  fragment: <span>{badge.fragment}</span>,
                }
              )}
            />
          )}

          {!hide?.trendingMenu && renderBySelector('trendingMenu', descendants)}
          {!hide?.breakingNews && renderBySelector('breakingNews', descendants)}

          <Article {...{ descendants }} {...$base} {...props} ref={ref}>
            {bodytext?.before ?? renderBySelector('lab-dz-2', descendants)}
            <Article.Bodytext {...{ descendants }} {...$bodytext} />
            {bodytext?.after}
          </Article>
        </section>

        {!hide?.secondNativeArticle && <SecondNativeArticle />}

        {!hide?.recommendedArticles && (
          <Article.Recommended {...$recommended}>
            <RecommendedArticles />
            <Strossle />
            {renderBySelector('affiliateGallery', descendants)}
            {showMoreRecommendedArticles && <div id="more-recommended-articles-portal" />}
          </Article.Recommended>
        )}
      </ErrorBoundary>
    );
  }
);

export const standalone = StandaloneArticle;

const DynamicStandaloneArticle = standaloneComponent('PostTypeArticle') as typeof standalone;

const LabradorArticle: LabradorComponent = ({ type, data, meta, descendants, ...props }) => {
  const { nativeAdLabel, isNativeAd } = meta ?? {};
  const { affiliateLabel, hasAffiliate, badgeData } = data ?? {};
  const { name: badgeName } = badgeData ?? {};
  const articleDisclosureLabels = [];

  if (isNativeAd && nativeAdLabel) {
    articleDisclosureLabels.push(nativeAdLabel);
  }

  if (hasAffiliate && affiliateLabel) {
    articleDisclosureLabels.push(affiliateLabel);
  }

  return (
    <DynamicStandaloneArticle
      hide={{ badge: true }}
      badge={{ fragment: badgeName }}
      {...{ descendants, articleDisclosureLabels }}
      {...props}
    />
  );
};

export default LabradorArticle;
